import { z } from "zod";

import { readStored, writeStored } from "@/lib/storage";

import {
  AccessDeniedError,
  type DrawingAccessApi,
  type NamedRole,
  type ProjectDetail,
  type ProjectSummary,
  type Restriction,
} from "../types";

/**
 * Per-drawing access in the mock (FR-COLLAB-008), as the identity service decides it
 * (`crates/services/identity/src/projects/restrictions.rs`): a restricted drawing opens for the project's admins
 * (Owners, Admins and the project lead), the people named and the roles named, and is left out of the project for
 * everyone else. The mock's standing: the team are Editors, everyone else who sees the project a Viewer. Kept in this
 * browser so a reload or another person signing in here sees the same rules.
 */

export const RESTRICTIONS_STORAGE_KEY = "orthos.mock.restrictions.v1";

const restrictionSchema = z.object({
  itemId: z.string(),
  kind: z.enum(["drawing", "file"]),
  people: z.array(z.string()),
  roles: z.array(z.enum(["viewer", "commenter", "editor"])),
  restrictedBy: z.string(),
  restrictedAt: z.string(),
});
/** Project ID to its restricted items. */
const storedSchema = z.record(z.string(), z.array(restrictionSchema));

type Standing = "manage" | NamedRole;

export interface MockRestrictionsOptions {
  /** The project as the signed-in person sees it (refuses the projects they do not). */
  find: (projectId: string) => ProjectSummary;
  /** Who is signed in, with their organisation role. */
  who: () => { personId: string; role: string };
  now: () => Date;
}

export interface MockRestrictions {
  api: DrawingAccessApi;
  /** The project as this person may read it: restricted drawings they are not let into are left out. */
  narrow(detail: ProjectDetail): ProjectDetail;
}

export function createMockRestrictions({
  find,
  who,
  now,
}: MockRestrictionsOptions): MockRestrictions {
  const read = (): Record<string, Restriction[]> =>
    readStored(RESTRICTIONS_STORAGE_KEY, storedSchema) ?? {};
  const write = (all: Record<string, Restriction[]>) => writeStored(RESTRICTIONS_STORAGE_KEY, all);

  const standing = (project: ProjectSummary): Standing => {
    const { personId, role } = who();
    if (role === "owner" || role === "admin" || project.team[0] === personId) return "manage";
    return project.team.includes(personId) ? "editor" : "viewer";
  };

  const reaches = (rule: Restriction, project: ProjectSummary): boolean => {
    const held = standing(project);
    return held === "manage" || rule.people.includes(who().personId) || rule.roles.includes(held);
  };

  const managed = (projectId: string): ProjectSummary => {
    const project = find(projectId);
    if (standing(project) !== "manage" || project.archived) throw new AccessDeniedError(projectId);
    return project;
  };

  const api: DrawingAccessApi = {
    list(projectId) {
      const project = find(projectId);
      const items = (read()[projectId] ?? []).filter((rule) => reaches(rule, project));
      return Promise.resolve({
        canManage: standing(project) === "manage" && !project.archived,
        items,
      });
    },
    restrict(projectId, itemId, rule) {
      managed(projectId);
      const all = read();
      const made: Restriction = {
        itemId,
        kind: rule.kind,
        people: [...new Set(rule.people)].sort(),
        roles: [...new Set(rule.roles)].sort(),
        restrictedBy: who().personId,
        restrictedAt: now().toISOString(),
      };
      all[projectId] = [...(all[projectId] ?? []).filter((item) => item.itemId !== itemId), made];
      write(all);
      return Promise.resolve(made);
    },
    lift(projectId, itemId) {
      managed(projectId);
      const all = read();
      all[projectId] = (all[projectId] ?? []).filter((item) => item.itemId !== itemId);
      write(all);
      return Promise.resolve();
    },
  };

  const narrow = (detail: ProjectDetail): ProjectDetail => {
    const rules = read()[detail.id] ?? [];
    const drawings = detail.drawings.flatMap((drawing) => {
      const rule = rules.find((item) => item.kind === "drawing" && item.itemId === drawing.id);
      if (rule === undefined) return [drawing];
      return reaches(rule, detail) ? [{ ...drawing, restricted: true }] : [];
    });
    return { ...detail, drawings };
  };

  return { api, narrow };
}
